import differenceInDays from 'date-fns/differenceInDays';
import {
  INVOICE_HUB_TABLES, INVOICE_STATUS_GROUP_MAPPING, INVOICE_STATUSES,
  RECORD_CNT, STATUS_QUERY_STRING, ORDER_BY_QUERY_STRING, INVOICES_DISCOUNT_TYPES,
} from '../consts/consts';
import { IInvoice } from '../../../../types';
import { getMonthShortName } from '../../../../helpers/datesAndTime.helper';

export const getNumOfOverdueDays = (dueDate: string) => differenceInDays(new Date(), new Date(dueDate));
export const isUnsentOverdueInvoice = (status: string, numOfOverdueDays: number) => (status === INVOICE_STATUSES.OUTSTANDING || status === INVOICE_STATUSES.SENT) && numOfOverdueDays > 0;

export const mapDataForInvoicesTable = (tableName: string, data: any) => {
  if (!data && !Array.isArray(data) && !data.length) return null;
  return data?.map((item: any) => (
    {
      invoice: item?.invoiceNumber,
      client: item?.invoiceRecipient?.name,
      clientId: item?.invoiceRecipient?.externalId,
      dueDate: item?.invoicePaymentTerm?.dueDate,
      amount: item?.invoiceService?.amount,
      discount: item?.invoiceService?.discount,
      discountType: item?.invoiceService?.discountType,
      salesTax: item?.invoiceService?.salesTax,
      status: item?.invoiceStatus,
      paidTime: item?.paidTime,
      externalId: item?.externalId,
      issueDate: item?.issueDate,
    }
  ));
};

export const mapDataForInvoicePage = (invoice: any) => {
  if (typeof invoice === 'undefined') return {};
  return {
    invoiceNumber: invoice?.invoiceNumber || 1,
    recipient: invoice?.invoiceRecipient?.name || '',
    dueDate: invoice?.invoicePaymentTerm?.dueDate || '',
    amount: invoice?.invoiceService?.amount || 0,
    discount: invoice?.invoiceService?.discount,
    discountType: invoice?.invoiceService?.discountType || '',
    salesTax: invoice?.invoiceService?.salesTax,
    status: invoice?.invoiceStatus || '',
    paidTime: invoice?.paidTime,
    paymentMethod: invoice?.invoicePayment?.[0]?.paymentMethod,
    paymentUrl: invoice?.paymentUrl,
    externalId: invoice?.externalId,
  };
};

//  @INPUT a date string: "2021-02-10T19:27:25.000Z", a boolean true/false for including/excluding the year
//  @OUTPUT a formatted date string: "10 Feb 2023" or "10 Feb 2023"
export const formatDate = (dateStr: string | undefined, includeYear: boolean, leadingMonth?: boolean) => {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  const dateOnly = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const year = dateOnly.getFullYear();
  const day = dateOnly.getDate();
  const monthNum = dateOnly.getMonth() + 1;
  const dayToDisplay = day.toString().length < 2 ? `0${day}` : day;
  const monthToDisplay = getMonthShortName(monthNum);
  const yearToDisplay = includeYear ? year : '';
  if (leadingMonth) return `${monthToDisplay} ${dayToDisplay} ${includeYear ? `, ${yearToDisplay}` : ''}`;
  return `${dayToDisplay} ${monthToDisplay} ${includeYear ? year : ''}`;
};

export const getInvoiceByExternalId = (invoicesArray: any, externalId: string) => invoicesArray.find((invoice:any) => invoice.externalId === externalId);

export const isInvoiceStatusSent = (status: string) => INVOICE_STATUS_GROUP_MAPPING.SENT.includes(status);
export const isInvoiceStatusUnsent = (status: string) => INVOICE_STATUS_GROUP_MAPPING.UNSENT.includes(status);
export const isInvoiceStatusPaid = (status: string) => INVOICE_STATUSES.PAID === status;
export const isInvoiceStatusCancelled = (status: string) => INVOICE_STATUSES.CANCEL === status;

export const getTableNamesByInvoiceStatus = (status: string) => {
  if (isInvoiceStatusPaid(status) || isInvoiceStatusCancelled(status)) return [INVOICE_HUB_TABLES.PAID.name, INVOICE_HUB_TABLES.CANCELED.name];
  if (isInvoiceStatusSent(status) || isInvoiceStatusUnsent(status)) return [INVOICE_HUB_TABLES.OUTSTANDING.name];
  return [''];
};

export const iFrameQueryParams = {
  BASE_URL: process.env.REACT_APP_HOST_ENV ? 'https://integ-invoice.lili.co/' : process.env.REACT_APP_NODE_ENV === 'production' ? 'https://invoice.lili.co/' : 'https://integ-invoice.lili.co/',
  CUSTOMIZATION_INVOICE: 'customizationInvoice',
  ORIGIN: 'origin',
  BRAND_COLOR: 'brandColor',
  LOGO_URL: 'logoUrl',
  CUSTOM_LOGO_PATH: 'customLogoPath',
  SHOW_BANK_ACCOUNT: 'showbankaccount',
  SHOW_CHECK_DETAILS: 'showcheckdetails',
  INVOICE_ID: 'invoice',
  CREATION_PROCESS: 'creationProcess',
};

export const getIframeUrl = (invoiceId: string | undefined) => {
  const baseUrl = new URL(iFrameQueryParams.BASE_URL);
  baseUrl.searchParams.set(iFrameQueryParams.INVOICE_ID, invoiceId || '');
  baseUrl.searchParams.set(iFrameQueryParams.CREATION_PROCESS, 'false');
  baseUrl.searchParams.set(iFrameQueryParams.ORIGIN, 'webApp');
  return baseUrl.href;
};

export const getInvoiceShareLink = (paymentUrl: string) => paymentUrl;

export const handleDownloadPDF = (assetUrl: any) => {
  window.open(assetUrl, '_blank');
  const elScript = document.createElement('a');
  elScript.href = assetUrl;
  elScript.setAttribute('download', 'Invoice');
  elScript.setAttribute('target', '_blank');
  elScript.click();
};

export const openPDFTab = (assetUrl: any) => {
  const a = document.createElement('a');
  a.href = assetUrl;
  a.target = '_blank';
  a.click();
};

export const buildQueryString = (tableName: string, pageNumber: number) => {
  if (tableName === INVOICE_HUB_TABLES.OUTSTANDING.name) return `${STATUS_QUERY_STRING.OUTSTANDING}&page=${pageNumber}&recordCnt=${RECORD_CNT}&${ORDER_BY_QUERY_STRING.OUTSTANDING}&sortDirection=ASC`;
  if (tableName === INVOICE_HUB_TABLES.PAID.name) return `${STATUS_QUERY_STRING.PAID}&page=${pageNumber}&recordCnt=${RECORD_CNT}&${ORDER_BY_QUERY_STRING.PAID}&sortDirection=ASC`;
  if (tableName === INVOICE_HUB_TABLES.CANCELED.name) return `${STATUS_QUERY_STRING.CANCELED}&page=${pageNumber}&recordCnt=${RECORD_CNT}&${ORDER_BY_QUERY_STRING.CANCELED}&sortDirection=ASC`;
  return '';
};

export const removeDuplicateObjectsByExternalId = (arrOfObjs: any) => (
  Array.from(new Set(arrOfObjs.map((item: any) => item.externalId)))
    .map(externalId => arrOfObjs.find((item: any) => item.externalId === externalId))
);

export const removeItemByExternalId = (arrOfObjs: any, externalId: string) => (
  arrOfObjs.filter((invoice: any) => invoice.externalId !== externalId)
);

export const scrollWebappToTop = () => {
  const webappElm = document.getElementById('webapp');
  webappElm?.scrollTo(0, 0);
};

export const calculateDiscountValue = (amount: number, discountType: string, discount: number) => {
  let discountValue = 0;
  if (!amount || !discountType || !discount) return discountValue;
  if (discountType === INVOICES_DISCOUNT_TYPES.FIXED) discountValue = discount;
  if (discountType === INVOICES_DISCOUNT_TYPES.PERCENT) discountValue = amount * discount;
  return discountValue;
};

export const calculateSalesTaxValue = (amount: number, discountValue: number, salesTax: number) => {
  if (!amount || !salesTax) return 0;
  return salesTax * (amount - discountValue);
};

export const calculateGrossAmount = (amount: number, discountType: string, discount: number, salesTax: number) => {
  const discountValue = calculateDiscountValue(amount, discountType, discount);
  const taxValue = calculateSalesTaxValue(amount, discountValue, salesTax);
  return amount - discountValue + taxValue;
};

export const hasInvoiceByExternalId = (invoicesList: IInvoice[], invoiceExternalId: string) => invoicesList.find((item: IInvoice) => item.externalId === invoiceExternalId);
